import type { CorrelationId } from "../kernel/identifiers.js";
import type { JsonObject } from "../kernel/json.js";

export enum ErrorCode {
  Validation = "VALIDATION_ERROR",
  Authentication = "AUTHENTICATION_ERROR",
  Authorization = "AUTHORIZATION_ERROR",
  NotFound = "NOT_FOUND",
  Conflict = "CONFLICT",
  Domain = "DOMAIN_ERROR",
  Calculation = "CALCULATION_ERROR",
  Persistence = "PERSISTENCE_ERROR",
  Integration = "INTEGRATION_ERROR",
  Internal = "INTERNAL_ERROR"
}

interface AppErrorOptions {
  readonly safeMessage?: string;
  readonly metadata?: JsonObject;
  readonly cause?: unknown;
}

export class AppError extends Error {
  public readonly code: ErrorCode;
  public readonly safeMessage: string;
  public readonly metadata: JsonObject;

  public constructor(
    code: ErrorCode,
    message: string,
    options: AppErrorOptions = {}
  ) {
    super(message, { cause: options.cause });
    this.name = "AppError";
    this.code = code;
    this.safeMessage = options.safeMessage ?? "Unable to complete the request";
    this.metadata = options.metadata ?? {};
  }
}

export interface SafeErrorResponse {
  readonly code: ErrorCode;
  readonly message: string;
  readonly correlationId: CorrelationId;
}

export function toSafeErrorResponse(
  error: unknown,
  correlationId: CorrelationId
): SafeErrorResponse {
  if (error instanceof AppError) {
    return {
      code: error.code,
      message: error.safeMessage,
      correlationId
    };
  }

  return {
    code: ErrorCode.Internal,
    message: "An unexpected error occurred",
    correlationId
  };
}
